import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, ImageIcon, Zap, Sparkles, Clock, X } from "lucide-react";

// -----------------------------------------------------------------------------
// TrialCountdownRibbon — sits at the very top of the Dashboard when the
// caller is in an active Stripe trial. Displays remaining days + a one-click
// upgrade CTA that jumps to the Stripe Customer Portal (where the user can
// switch plans, add payment method, or cancel). Dismissible per-session via
// sessionStorage; re-appears on next login.
// -----------------------------------------------------------------------------
export function TrialCountdownRibbon() {
  const [snap, setSnap] = useState(null);
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== "undefined" && sessionStorage.getItem("bf360.trial_dismiss") === "1"
  );
  useEffect(() => {
    api.get("/pricing/me").then(({ data }) => setSnap(data)).catch(() => {});
  }, []);
  if (!snap || dismissed || !snap.trial_end_at) return null;
  const endsAt = new Date(snap.trial_end_at);
  const now = new Date();
  const msLeft = endsAt.getTime() - now.getTime();
  if (msLeft <= 0) return null;  // trial ended, ribbon irrelevant
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  const hoursLeft = Math.ceil(msLeft / (1000 * 60 * 60));
  const label = daysLeft <= 1
    ? `${hoursLeft} hour${hoursLeft === 1 ? "" : "s"} left`
    : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
  const urgent = daysLeft <= 2;

  const openPortal = async () => {
    try {
      const { data } = await api.post("/pricing/portal");
      window.location.href = data.portal_url;
    } catch {
      window.location.href = "/pricing";
    }
  };

  return (
    <div className={`${urgent ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-emerald-50 border-emerald-300 text-emerald-900"} border-b flex items-center justify-between px-6 py-3`}
         data-testid="trial-ribbon">
      <div className="flex items-center gap-3">
        <Clock className={`w-4 h-4 ${urgent ? "text-amber-700" : "text-emerald-700"}`}/>
        <span className="text-sm">
          <strong data-testid="trial-days-left">{label}</strong> in your{" "}
          <span className="capitalize">{snap.plan_key}</span> trial —{" "}
          {urgent ? "add a payment method to keep formatting" : "cancel anytime, no charge until day 7"}.
        </span>
      </div>
      <div className="flex items-center gap-4">
        <button onClick={openPortal}
                className={`text-xs px-3 py-1.5 font-medium ${urgent ? "bg-amber-800 text-white hover:bg-amber-900" : "bg-emerald-700 text-white hover:bg-emerald-800"}`}
                data-testid="trial-manage-cta">
          Manage subscription
        </button>
        <button onClick={() => {
                  sessionStorage.setItem("bf360.trial_dismiss", "1");
                  setDismissed(true);
                }}
                className="text-current opacity-60 hover:opacity-100"
                title="Dismiss for this session"
                data-testid="trial-dismiss">
          <X className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}


/**
 * Compact credit meter shown on the Dashboard.
 * Reads /api/pricing/me and renders two thin progress bars — book credits
 * used / hero image credits used — plus an upgrade nudge when either
 * meter is 80%+ full.
 */
export function CreditMeter() {
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    api.get("/pricing/me").then(({ data }) => setSnap(data)).catch(() => {});
  }, []);
  if (!snap) return null;
  const { plan_key, limits, usage, remaining } = snap;
  const bookPct = limits.books ? Math.min(100, Math.round((usage.books_used / limits.books) * 100)) : 0;
  const heroPct = limits.heroes ? Math.min(100, Math.round((usage.heroes_used / limits.heroes) * 100)) : 100;
  const showUpgrade = plan_key !== "studio" && (bookPct >= 80 || heroPct >= 80);

  return (
    <div className="border border-[color:var(--border)] bg-white p-6 mb-8" data-testid="credit-meter">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Current plan</p>
          <p className="text-2xl font-serif capitalize mt-1" data-testid="credit-meter-plan">{plan_key}</p>
        </div>
        <Link to="/pricing" className="text-xs uppercase tracking-widest text-[color:var(--primary)] hover:underline" data-testid="credit-meter-manage">
          {plan_key === "studio" ? "Manage" : "Upgrade →"}
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-2 text-xs text-[color:var(--text-muted)] mb-2">
            <BookOpen className="w-3 h-3"/>
            <span data-testid="credit-books-count">{usage.books_used} / {limits.books} books</span>
          </div>
          <div className="h-1.5 bg-[color:var(--canvas)] overflow-hidden">
            <div className={`h-full transition-all ${bookPct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                 style={{ width: `${bookPct}%` }}/>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 text-xs text-[color:var(--text-muted)] mb-2">
            <ImageIcon className="w-3 h-3"/>
            <span data-testid="credit-heroes-count">{usage.heroes_used} / {limits.heroes} hero images</span>
          </div>
          <div className="h-1.5 bg-[color:var(--canvas)] overflow-hidden">
            <div className={`h-full transition-all ${heroPct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                 style={{ width: `${heroPct}%` }}/>
          </div>
        </div>
      </div>
      {showUpgrade && (
        <div className="mt-4 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 border border-amber-200">
          <Zap className="w-3 h-3"/>
          <span data-testid="credit-meter-upgrade-nudge">
            You're at {Math.max(bookPct, heroPct)}% of your monthly allowance —{" "}
            <Link to="/pricing" className="underline">upgrade to keep formatting</Link>.
          </span>
        </div>
      )}
      {/* iter-73 — Overage ledger. Only paid plans overage-bill; the
          Dashboard surfaces month-to-date pending charges so authors
          never get an invoice surprise. */}
      {snap.overage && snap.overage.pending_total_cents > 0 && (
        <div className="mt-3 flex items-center justify-between text-xs bg-slate-50 border border-slate-200 px-3 py-2"
             data-testid="credit-meter-overage-row">
          <span>
            <strong>{snap.overage.books}</strong> overage book{snap.overage.books === 1 ? "" : "s"}
            {snap.overage.heroes > 0 && <> · <strong>{snap.overage.heroes}</strong> extra hero image{snap.overage.heroes === 1 ? "" : "s"}</>}{" "}
            billed at your next invoice
          </span>
          <span className="font-medium">
            +${(snap.overage.pending_total_cents / 100).toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
}


/**
 * Ready-to-publish health card. Reads the currently-active book's status
 * and renders a friendly 5-point checklist that turns green as the author
 * ticks each requirement off. Used on the BookEditor top-bar area, not
 * on the Dashboard.
 */
export function PublishReadyCard({ book }) {
  if (!book) return null;
  const hasCover = !!(book.cover_data_url || book.cover_url);
  const hasChapters = (book.chapters || []).length > 0;
  const allLocked = hasChapters
    && (book.chapters || []).every((c) => c.chapter_source_index == null
      || (book.formatter_v2_locked_blocks || {})[String(c.chapter_source_index)]);
  const hasFront = ((book.front_matter_sections || []).some((s) => s.include && (s.content || "").trim())
                    || !!(book.front_matter && (book.front_matter.title || book.front_matter.content)));
  const hasBack = (book.back_matter_sections || []).some((s) => s.include && (s.content || "").trim());
  const items = [
    { done: hasCover,     label: "Cover uploaded" },
    { done: hasChapters,  label: `${(book.chapters || []).length} chapter(s) present` },
    { done: hasFront,     label: "Front matter drafted" },
    { done: hasBack,      label: "Back matter drafted" },
    { done: allLocked,    label: "All imported chapters locked" },
  ];
  const doneCount = items.filter((i) => i.done).length;
  const total = items.length;
  const pct = Math.round((doneCount / total) * 100);
  const ready = doneCount === total;
  return (
    <div className={`border p-5 mb-6 ${ready ? "border-emerald-400 bg-emerald-50" : "border-[color:var(--border)] bg-white"}`}
         data-testid="publish-ready-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className={`w-4 h-4 ${ready ? "text-emerald-600" : "text-[color:var(--primary)]"}`}/>
          <span className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">
            Ready to publish
          </span>
        </div>
        <span className={`text-2xl font-serif ${ready ? "text-emerald-700" : "text-[color:var(--text)]"}`}
              data-testid="publish-ready-percent">{pct}%</span>
      </div>
      <div className="h-1 bg-[color:var(--canvas)] mb-4 overflow-hidden">
        <div className={`h-full transition-all ${ready ? "bg-emerald-500" : "bg-[color:var(--primary)]"}`}
             style={{ width: `${pct}%` }}/>
      </div>
      <ul className="space-y-1.5 text-xs">
        {items.map((item, i) => (
          <li key={i} className={`flex items-center gap-2 ${item.done ? "text-[color:var(--text)]" : "text-[color:var(--text-muted)]"}`}
              data-testid={`publish-item-${i}`}>
            <span className={`inline-block w-3 h-3 border ${item.done ? "bg-emerald-500 border-emerald-500" : "border-[color:var(--border)]"}`}/>
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
